import { Edit, useForm } from "@refinedev/antd";
import { HttpError } from "@refinedev/core";
import {
  Alert,
  Card,
  Form,
  Select,
  Space,
  Spin,
  TimePicker,
  Typography,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { apiUrlV2 } from "@/consts";
import { TOKEN_KEY } from "@/authProvider";

const { Text } = Typography;

const TYPE_LABELS: Record<string, string> = {
  TRAINING: "Training",
  FIXTURE: "Fixture",
  STRENGTH_CONDITIONING: "Strength & Conditioning",
  RECOVERY: "Recovery",
  MEETING: "Meeting",
};

const WEEKDAY_OPTIONS = [
  { label: "Mon", value: "MON" },
  { label: "Tue", value: "TUE" },
  { label: "Wed", value: "WED" },
  { label: "Thu", value: "THU" },
  { label: "Fri", value: "FRI" },
  { label: "Sat", value: "SAT" },
  { label: "Sun", value: "SUN" },
];

type AddressDTO = {
  placeId?: string | null;
  placeProvider?: string;
  placeUrl?: string | null;
  formattedAddress?: string | null;
  line1: string;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  countryCode: string;
  latitude?: number | null;
  longitude?: number | null;
};

// Unified Places search (GYM-772) lives on the v1 API, not the admin envelope.
async function searchVenues(q: string): Promise<AddressDTO[]> {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(
    `${apiUrlV2}/address/search?q=${encodeURIComponent(q)}&limit=8`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) return [];
  const body = await res.json();
  return body.list ?? [];
}

function VenuePicker({
  initialAddress,
  onChange,
}: {
  initialAddress: any;
  onChange: (addr: AddressDTO | null, existingId: string | null) => void;
}) {
  const [options, setOptions] = useState<{ label: string; value: string; addr: AddressDTO }[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string | undefined>(
    initialAddress?.formattedAddress || initialAddress?.line1 || undefined
  );

  let timer: ReturnType<typeof setTimeout>;
  const handleSearch = (value: string) => {
    clearTimeout(timer);
    if (!value || value.trim().length < 3) {
      setOptions([]);
      return;
    }
    setLoading(true);
    timer = setTimeout(async () => {
      const results = await searchVenues(value.trim());
      setOptions(
        results.map((a, i) => ({
          label: a.formattedAddress || a.line1,
          value: a.placeId || `${a.line1}-${i}`,
          addr: a,
        }))
      );
      setLoading(false);
    }, 350);
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      {selectedLabel && (
        <Text type="secondary">Current venue: {selectedLabel}</Text>
      )}
      <Select
        showSearch
        placeholder="Search for a venue address"
        filterOption={false}
        onSearch={handleSearch}
        notFoundContent={loading ? <Spin size="small" /> : null}
        style={{ width: "100%" }}
        options={options}
        onChange={(value) => {
          const picked = options.find((o) => o.value === value);
          if (picked) {
            setSelectedLabel(picked.label);
            onChange(picked.addr, null);
          }
        }}
      />
    </Space>
  );
}

export function SessionTypeEdit() {
  const { formProps, saveButtonProps, query, formLoading } = useForm<any, HttpError, any>({
    resource: "session_type",
    action: "edit",
    redirect: "list",
    successNotification: (data) => ({
      message: data?.data?.message ?? "Saved",
      type: "success",
    }),
  });

  // getOne returns the record directly (dataProvider unwraps to query.data.data).
  const record = query?.data?.data;

  const [timeMode, setTimeMode] = useState<string>("EDITABLE");
  const [locationMode, setLocationMode] = useState<string>("EDITABLE");
  // Venue selection captured outside the antd form fields.
  const [pickedAddress, setPickedAddress] = useState<AddressDTO | null>(null);
  const [existingAddressId, setExistingAddressId] = useState<string | null>(null);

  useEffect(() => {
    if (record) {
      setTimeMode(record.timeMode);
      setLocationMode(record.locationMode);
      setExistingAddressId(record.fixedAddressID ?? null);
    }
  }, [record]);

  const initialWindow = useMemo(() => {
    if (record?.fixedStartTime && record?.fixedEndTime) {
      return [
        dayjs(record.fixedStartTime, "HH:mm"),
        dayjs(record.fixedEndTime, "HH:mm"),
      ];
    }
    return undefined;
  }, [record]);

  // Transform the form values into the PATCH payload our admin endpoint expects.
  const handleFinish = (values: any) => {
    const payload: any = {
      timeMode: values.timeMode,
      locationMode: values.locationMode,
      allowedWeekdays: values.allowedWeekdays ?? [],
    };

    if (values.timeMode === "FIXED" && values.window) {
      payload.fixedStartTime = dayjs(values.window[0]).format("HH:mm");
      payload.fixedEndTime = dayjs(values.window[1]).format("HH:mm");
    }

    if (values.locationMode === "FIXED") {
      if (pickedAddress) {
        payload.address = pickedAddress;
      } else if (existingAddressId) {
        payload.fixedAddressID = existingAddressId;
      }
    }

    formProps.onFinish?.(payload);
  };

  return (
    <Edit
      saveButtonProps={saveButtonProps}
      isLoading={formLoading}
      resource="session_type"
      title={`Edit ${TYPE_LABELS[record?.type] ?? record?.type ?? "Session Type"}`}
      recordItemId={record?.type}
    >
      <Form
        {...formProps}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          timeMode: record?.timeMode,
          locationMode: record?.locationMode,
          allowedWeekdays: record?.allowedWeekdays ?? [],
          window: initialWindow,
        }}
      >
        <Card title="Time Policy" size="small" style={{ marginBottom: 16 }}>
          <Form.Item label="Time Mode" name="timeMode" rules={[{ required: true }]}>
            <Select onChange={(v) => setTimeMode(v)}>
              <Select.Option value="EDITABLE">Editable (coach picks)</Select.Option>
              <Select.Option value="FIXED">Fixed window</Select.Option>
            </Select>
          </Form.Item>

          {timeMode === "FIXED" && (
            <Form.Item
              label="Fixed Start / End"
              name="window"
              rules={[{ required: true, message: "Pick a start and end time" }]}
            >
              <TimePicker.RangePicker format="HH:mm" minuteStep={5} />
            </Form.Item>
          )}
        </Card>

        <Card title="Location Policy" size="small" style={{ marginBottom: 16 }}>
          <Form.Item
            label="Location Mode"
            name="locationMode"
            rules={[{ required: true }]}
          >
            <Select onChange={(v) => setLocationMode(v)}>
              <Select.Option value="EDITABLE">Editable (coach picks)</Select.Option>
              <Select.Option value="FIXED">Fixed venue</Select.Option>
            </Select>
          </Form.Item>

          {locationMode === "FIXED" && (
            <Form.Item label="Fixed Venue">
              <VenuePicker
                initialAddress={record?.fixedAddress}
                onChange={(addr, id) => {
                  setPickedAddress(addr);
                  setExistingAddressId(id ?? existingAddressId);
                }}
              />
              {!pickedAddress && !existingAddressId && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 8 }}
                  message="A fixed venue is required when location is FIXED."
                />
              )}
            </Form.Item>
          )}
        </Card>

        <Card title="Allowed Weekdays" size="small">
          <Form.Item
            label="Days this session type may fall on"
            name="allowedWeekdays"
            help="Leave empty to allow every day."
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="Select weekdays"
              options={WEEKDAY_OPTIONS}
            />
          </Form.Item>
        </Card>
      </Form>
    </Edit>
  );
}
